import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {HeadingWithDecoration} from '../../common/HeadingWithDecoration';
import styles from './acknowledgement.module.scss';
import {useParams} from 'react-router-dom';
import {Checkbox, MessageBar, MessageBarType, Spinner} from '@fluentui/react';
import {TheButton} from '../../common/TheButton';
import {useAcknowledgementBatch} from '../../../hooks/services/useAcknowledgementBatch';
import {BatchAcknowledgement} from '../../../models/Acknowledgement';
import {EnhancedDetailsList, OtherColumn, SelectColumn, StringColumn} from '../../common/EnhancedDetailsList';
import {IEnhancedColumn} from '../../common/EnhancedDetailsList/IEnhancedDetailsList';
import {BatchAcknowledgementElementDto} from '../../../services/generated/models/BatchAcknowledgementElementDto';
import {UnitDto} from '../../../services/generated';
import { BatchAcknowledgementElementModel } from '../../../models/BatchAcknowledgementElementModel';

function getProtocol(extension: string | null): string | undefined {
  if (!extension) return;
  switch (extension.toLowerCase()) {
    case 'doc':
    case 'dot':
    case 'wbk':
    case 'docx':
    case 'docm':
    case 'dotx':
    case 'dotm':
    case 'docb':
    case 'wll':
    case 'wwl':
      return 'ms-word:';
    case 'ppt':
    case 'pot':
    case 'pps':
    case 'ppa':
    case 'ppam':
    case 'pptx':
    case 'pptm':
    case 'potx':
    case 'potm':
    case 'ppsx':
    case 'ppsm':
    case 'sldx':
    case 'sldm':
    case 'pa':
      return 'ms-powerpoint:';
    case 'xls':
    case 'xlt':
    case 'xlm':
    case 'xll_':
    case 'xla_':
    case 'xla5':
    case 'xla8':
    case 'xlsx':
    case 'xlsm':
    case 'xltx':
    case 'xltm':
    case 'xlsb':
    case 'xla':
    case 'xlam':
    case 'xll':
    case 'xlw':
      return 'ms-excel:';
    case 'vsd':
    case 'vss':
    case 'vst':
    case 'vdw':
    case 'vdx':
    case 'vsx':
    case 'vtx':
    case 'vsdx':
    case 'vsdm':
    case 'vssx':
    case 'vssm':
    case 'vstx':
    case 'vstm':
      return 'ms-visio:';
    case 'adn':
    case 'accdb':
    case 'accdr':
    case 'accdt':
    case 'accda':
    case 'mdw':
    case 'accde':
    case 'mam':
    case 'maq':
    case 'mar':
    case 'mat':
    case 'maf':
    case 'laccdb':
    case 'ade':
    case 'adp':
    case 'mdb':
    case 'cdb':
    case 'mda':
    case 'mdn':
    case 'mdf':
    case 'mde':
    case 'ldb':
      return 'ms-access:';
    case 'mpp':
    case 'mpt':
    case 'mpd':
    case 'mpw':
    case 'mpx':
    case 'mspdi':
      return 'ms-project:';
    case 'pub':
      return 'ms-publisher:';
    case 'dwt':
    case 'fwp':
      return 'ms-spd:';
    case 'xsn':
      return 'ms-infopath:';
  }
}

export const Acknowledgement: FC = () => {
  const {packageId} = useParams();
  const packageItemId = Number(packageId);

  const {
    getAcknowledgementBatch,
    putAcknowledgementOpened,
    putAcknowledgementConfirmed,
    putAcknowledgementFinished
  } = useAcknowledgementBatch();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [acknowledgementData, setAcknowledgementData] = useState<BatchAcknowledgement>();

  const [messageBar, setMessageBar] = useState<{errorMsg: JSX.Element} | undefined>();
  const [renderKey, setRenderKey] = useState<number>(0);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const [acknowledgementItems, setAcknowledgementItems] = useState<
    BatchAcknowledgementElementModel[]
  >([]);
  const [btnFinishDisable, setBtnFinishDisable] = useState<boolean>(true);

  const setterBtnFinishStatus = (items: BatchAcknowledgementElementDto[]) => {
    if (items.length) {
      const allItemsChecked = items.every(item => item.opened && item.confirmed);
      setBtnFinishDisable(!allItemsChecked);
    }
  };

  const getAcknowledgementData = useCallback(async () => {
    if (!packageItemId) {
      return;
    }
    const response = await getAcknowledgementBatch(packageItemId);
    if (response.result) {
      const data = response.result.elements;
      setAcknowledgementData(response.result);
      setAcknowledgementItems(response.result.elements.map(item => new BatchAcknowledgementElementModel(item)));
      setIsReadOnly(response.result.readOnly);
      setterBtnFinishStatus(data);
      setRenderKey(prev => prev + 1);
    }
    setIsLoading(false);
  }, [getAcknowledgementBatch, packageItemId]);

  const onOpenElementHandler = useCallback(
    async (item: BatchAcknowledgementElementModel) => {
      if (item.opened || isReadOnly) {
        return;
      }
      setIsLoading(true);
      setMessageBar(undefined);
      const result = await putAcknowledgementOpened(item.id);
      if (result.apiCode) {
        setMessageBar({
          errorMsg: (
            <MessageBar messageBarType={MessageBarType.error}>
              Something went wrong. Error during saving opened status, code: {result.apiCode}
            </MessageBar>
          )
        });
      }
      getAcknowledgementData();
      setIsLoading(false);
    },
    [getAcknowledgementData, putAcknowledgementOpened, isReadOnly]
  );

  const getUnitTitle = (unit: UnitDto | null): string => {
    if (!unit || !unit.title) {
      return '';
    }
    return unit.parentTitle ? `${unit.parentTitle} | ${unit.title}`.trim() : unit.title;
  };

  const buildDocumentLink = (url: string) => {
    const extensionRegex = url.match(/\.(\w{2,5})(?:$|\?)/);
    const extension = extensionRegex ? extensionRegex[1] : null;
    const protocol = getProtocol(extension);
    const appLink = protocol ? `${protocol}ofv|u|${url}` : undefined;
    const isVisio = protocol === 'ms-visio:';
    const openLink =
    extension === 'pdf' || !extension ? url : `${url}${url.indexOf('?') === -1 ? '?' : '&'}web=1`;
     const whatAsDefaultAction = isVisio || !appLink ?  openLink : appLink;
    return whatAsDefaultAction;
  };

  const onConfirmItemSave = useCallback(
    async (id: number) => {
      setIsLoading(true);
      const result = await putAcknowledgementConfirmed(id);
      if (!result.apiCode) {
        getAcknowledgementData();
      }
      setIsLoading(false);
    },
    [getAcknowledgementData, putAcknowledgementConfirmed]
  );

  const onConfirmHandler = useCallback(
    (item: BatchAcknowledgementElementDto, isChecked: boolean) => {
      setMessageBar(undefined);
      if (isChecked && !item.opened) {
        setMessageBar({
          errorMsg: (
            <MessageBar messageBarType={MessageBarType.error}>
              Sorry but you can't mark a element as acknowledged before you have read it. Check if
              all elements were opened.
            </MessageBar>
          )
        });
        return;
      }
      onConfirmItemSave(item.id);
    },
    [onConfirmItemSave]
  );

  const onFinishConfirmationSave = async () => {
    setIsLoading(true);
    const result = await putAcknowledgementFinished(packageItemId);
    if (!result.apiCode) {
      getAcknowledgementData();
    }
    setIsLoading(false);
  };

  const onFinishConfirmationHandler = () => {
    setMessageBar(undefined);
    const allItemsChecked = acknowledgementItems.every(item => item.opened && item.confirmed);
    if (!allItemsChecked) {
      setMessageBar({
        errorMsg: (
          <MessageBar messageBarType={MessageBarType.error}>
            Sorry but you didn't check all elements as opened and acknowledged. The form was not
            sent
          </MessageBar>
        )
      });
      return;
    }
    onFinishConfirmationSave();
  };
  const columnsView = useMemo(() => {
    const columns: IEnhancedColumn<BatchAcknowledgementElementModel>[] = [
      {
        key: 'title',
        name: 'Title',
        data: StringColumn,
        minWidth: 400,
        onRender(item) {
          return item.isProcess ? (
            <a
              href={`/unit/${acknowledgementData?.unit?.id}/process/${item.databaseElementId}`}
              target="_blank"
              rel="noreferrer"
              className={styles.link}
              onClick={() => onOpenElementHandler(item)}>
              {item.title}
            </a>
          ) : item.relativeUrl ? (
            <a
              href={buildDocumentLink(item.relativeUrl)}
              target="_blank"
              rel="noreferrer"
              className={styles.link}
              onClick={() => onOpenElementHandler(item)}>
              {item.title}
            </a>
          ) : (
            <span>{item.title}</span>
          );
        }
      },
      {
        key: 'typeString',
        name: 'Type',
        data: SelectColumn,
        minWidth: 220,
        onRender(item) {
          return <span>{item.isProcess ? 'Process' : 'Document'}</span>;
        }
      },
      {
        key: 'documentId',
        name: 'Document Id',
        data: StringColumn,
        minWidth: 220
      },
      {
        key: 'languageCode',
        name: 'Language',
        data: StringColumn,
        minWidth: 145
      },
      {
        key: 'opened',
        name: 'Opened',
        data: OtherColumn,
        minWidth: 120,
        onRender(item) {
          return (
            <Checkbox disabled={true} className={styles.select} defaultChecked={item.opened} />
          );
        }
      },
      {
        key: 'confirmed',
        name: 'Acknowledged',
        data: OtherColumn,
        minWidth: 120,
        onRender(item) {
          return item.opened ? (
            <Checkbox
              className={styles.select}
              onChange={(ev, checked) => onConfirmHandler(item, !!checked)}
              defaultChecked={item.confirmed}
              disabled={isReadOnly}
            />
          ) : (
            <Checkbox
              className={styles.select}
              onChange={() =>
                setMessageBar({
                  errorMsg: (
                    <MessageBar messageBarType={MessageBarType.severeWarning}>
                      Sorry but you can't confirm until it is opend
                    </MessageBar>
                  )
                })
              }
              disabled={isReadOnly}
              checked={false}
            />
          );
        }
      }
    ];
    return columns;
  }, [isReadOnly, onConfirmHandler, onOpenElementHandler, acknowledgementData?.unit?.id]);

  useEffect(() => {
    getAcknowledgementData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const header = useMemo(() => {
    const text = acknowledgementData?.unit ? 'elements' : 'documents';
    return 'You are required to acknowledge that you have read and understood the below ' + text;
  }, [acknowledgementData?.unit]);

  let contentJSX: JSX.Element;
  if (isLoading) {
    contentJSX = <Spinner />;
  } else if (!isLoading && acknowledgementData) {
    const {comment, process, elements, readOnly, unit} = acknowledgementData;

    const deadlineHeaderJSX = (
      <div className={styles.acknowledgementDetails}>
        <h6>Deadline</h6>
        <p>{acknowledgementData.deadline?.split('T')[0]}</p>
      </div>
    );

    const commentJSX = comment && (
      <div className={styles.acknowledgementDetails}>
        <h6>Background / Reason </h6>
        <div
          dangerouslySetInnerHTML={{
            __html: comment
          }}></div>
      </div>
    );

    const processDetailsJSX = process && (
      <div className={styles.acknowledgementDetails}>
        <h6>Process Details</h6>
        <p>
          <a
            target="_blank"
            rel="noreferrer"
            href={`/unit/${process.unitId}/process/${process.id}`}>
            {process.title}
          </a>
        </p>
      </div>
    );

    const unitDetailsJSX = unit && (
      <div className={styles.acknowledgementDetails}>
        <h6>Unit Details</h6>
        <p>
          <a target="_blank" rel="noreferrer" href={`/unit/${unit.id}`}>
            {getUnitTitle(unit)}
          </a>
        </p>
      </div>
    );

    contentJSX = (
      <>
        <h4 className={styles.acknowledgementExplanation}>{header}</h4>
        {commentJSX}
        {deadlineHeaderJSX}
        {processDetailsJSX}
        {unitDetailsJSX}
        {messageBar?.errorMsg}
        <EnhancedDetailsList
          key={renderKey}
          items={elements.map(item => new BatchAcknowledgementElementModel(item))}
          columns={columnsView}
          hideSearch
          frozen
          pageSize={30}
        />
        {!readOnly && (
          <footer>
            <TheButton primary onClick={onFinishConfirmationHandler} disabled={btnFinishDisable}>
              Confirm Complete
            </TheButton>
          </footer>
        )}
      </>
    );
  } else {
    contentJSX = <h4>No data to show</h4>;
  }
  return (
    <main className={styles.main}>
      <div className={styles.mainWrapper}>
        <HeadingWithDecoration text="Acknowledgement" />
        {contentJSX}
      </div>
    </main>
  );
};
